import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Unlock } from 'lucide-react';

const STAGE_TITLES = {
  1: 'Brand details',
  2: 'Generate brief from trends',
  3: 'Save inspiration videos',
  4: 'Generate brief from a video',
  5: 'Manage the Content Board'
};

function ConfettiBurst() {
  const [parts, setParts] = useState([]);
  useEffect(() => {
    const count = 60;
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4'];
    setParts(
      Array.from({ length: count }, (_, i) => ({
        id: i,
        x: Math.random() * 100 - 50,
        y: Math.random() * 100 - 50,
        color: colors[i % colors.length],
        delay: Math.random() * 0.2,
        duration: 1.2 + Math.random() * 0.6,
        size: 6 + Math.random() * 6,
        rotation: Math.random() * 360
      }))
    );
  }, []);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {parts.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-sm"
          style={{
            left: '50%',
            top: '50%',
            width: p.size,
            height: p.size * 0.6,
            backgroundColor: p.color,
            boxShadow: `0 0 ${p.size}px ${p.color}`
          }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{
            x: p.x * 8,
            y: p.y * 8 + 200,
            opacity: 0,
            rotate: p.rotation + 360
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeOut'
          }}
        />
      ))}
    </div>
  );
}

export default function OnboardingCelebration({ stage, isOpen, onClose }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const nextStage = stage + 1;
  const nextTitle = STAGE_TITLES[nextStage];
  const isLast = stage >= 5;

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 2500);
      return () => clearTimeout(t);
    }
  }, [isOpen, stage]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
        {showConfetti && <ConfettiBurst />}
        <motion.div
          className="relative bg-zinc-900 border-2 border-indigo-500 rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-8 text-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <div className="flex justify-center mb-4">
            <motion.div
              className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Trophy className="h-8 w-8 text-amber-400" />
            </motion.div>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Stage {stage} complete!</h2>
          <p className="text-amber-400 font-medium mb-4">Unlocked</p>
          {isLast ? (
            <p className="text-zinc-300 text-sm mb-6">You&apos;ve completed onboarding. You&apos;re ready to create!</p>
          ) : (
            <p className="text-zinc-300 text-sm mb-6">
              <Unlock className="inline h-4 w-4 mr-1 text-indigo-400" />
              <span className="font-medium text-indigo-300">{nextTitle}</span>
            </p>
          )}
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-colors"
          >
            {isLast ? "Let's go" : 'Continue'}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export { STAGE_TITLES };
